import React from 'react';
import SxWindow from '../SxWindow';
import { useVisibleTicker } from '../useVisibleTicker';

/* 各产品品牌色（原为 #1677FF）统一收到强调色：
   五个演示在同一页滚动出现，各用自己的色会让强调色失去指示作用。
   SVG 的 stroke/fill 不吃 CSS 变量的回退链，这里取字面值。 */
const BRAND = '#ff6308';

/** 公域到私域的四段闭环，对应产品真实的执行链路 */
const PIPELINE = [
  { label: '选题成片', detail: '行业话术库 → 口播脚本 → 合规审核' },
  { label: '渠道发布', detail: '抖音真机 RPA 发布' },
  { label: '线索捕获', detail: '评论 / 私信进线识别' },
  { label: '私域承接', detail: '引导加微 → 接待建议' },
];

const METRICS = [
  { label: '今日曝光', value: '12,480', delta: '+18%' },
  { label: '新增线索', value: '37', delta: '+9' },
  { label: 'ROI', value: '3.4x', delta: '+0.6' },
];

const PENDING = [
  { text: '「暑期班续报」口播稿待确认', tag: '待拍板' },
  { text: 3, tag: '高意向线索待跟进' },
];

/**
 * 销售智能体 · 老板看板模拟。
 *
 * 结构上最接近参考站的 Computer 演示：一个带运行状态的窗口，
 * 内部展示 agent 正在自主执行的流水线。活跃步骤随时间轮转，
 * 传达"它在替你干活，你只需要拍板"这个核心主张。
 */
const SalesAgentDemo: React.FC = () => {
  const { ref, tick } = useVisibleTicker(1900);
  const activeStep = tick % PIPELINE.length;

  return (
    <div ref={ref}>
      <SxWindow title="销售智能体 · 经营看板" status="working">
        {/* 指标行 */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {METRICS.map((m) => (
            <div
              key={m.label}
              className="rounded-[10px] border p-2.5 md:p-3"
              style={{ borderColor: 'var(--sx-line)', background: 'var(--sx-surface-2)' }}
            >
              <p className="sx-mono text-[9px] md:text-[10px] mb-1.5">{m.label}</p>
              <p className="sx-num text-base md:text-xl font-medium text-white leading-none">
                {m.value}
              </p>
              <p className="sx-num text-[9px] md:text-[10px] mt-1.5" style={{ color: BRAND }}>
                {m.delta}
              </p>
            </div>
          ))}
        </div>

        {/* 执行流水线：活跃步骤轮转 */}
        <div className="space-y-1.5">
          {PIPELINE.map((step, i) => {
            const isActive = i === activeStep;
            const isDone = i < activeStep;
            return (
              <div
                key={step.label}
                className="flex items-center gap-2.5 rounded-[10px] px-2.5 py-2 transition-colors duration-500"
                style={{
                  background: isActive ? 'rgba(22,119,255,0.08)' : 'transparent',
                  border: `1px solid ${isActive ? 'rgba(22,119,255,0.28)' : 'var(--sx-line)'}`,
                }}
              >
                <span
                  className="sx-num text-[9px] w-4 shrink-0 text-center"
                  style={{ color: isActive ? BRAND : 'var(--sx-mute)' }}
                >
                  {isDone ? '✓' : String(i + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className="text-[11px] md:text-xs font-medium leading-tight truncate transition-colors duration-500"
                    style={{ color: isActive ? '#fff' : 'var(--sx-dim)' }}
                  >
                    {step.label}
                  </p>
                  <p className="sx-mono sx-mono-plain text-[8px] md:text-[9px] mt-0.5 truncate">
                    {step.detail}
                  </p>
                </div>
                {isActive && (
                  <span
                    className="sx-dot sx-dot-live shrink-0"
                    style={{ background: BRAND }}
                    aria-hidden="true"
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* 待拍板：强调"人保留决策权" */}
        <div className="mt-4 pt-3.5" style={{ borderTop: '1px solid var(--sx-line)' }}>
          <p className="sx-mono text-[9px] mb-2.5">等待你拍板</p>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] md:text-xs truncate" style={{ color: 'var(--sx-dim)' }}>
                {PENDING[0].text}
              </p>
              <span className="sx-chip text-[8px] shrink-0" style={{ padding: '2px 6px' }}>
                {PENDING[0].tag}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="sx-num text-[11px] md:text-xs" style={{ color: 'var(--sx-accent)' }}>
                {PENDING[1].text}
              </span>
              <p className="text-[11px] md:text-xs" style={{ color: 'var(--sx-dim)' }}>
                {PENDING[1].tag}
              </p>
            </div>
          </div>
        </div>
      </SxWindow>
    </div>
  );
};

export default SalesAgentDemo;
